import * as Sentry from "@sentry/react";
import { Logger } from "@/lib/logger";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { useMaintenanceStore, type ApiOutageCause } from "@/store/maintenance.store";

const logger = new Logger("API");

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? "/api";

// --- Mode maintenance déploiement -----------------------------------------
// Pendant un `make deploy`, l'API bascule en maintenance Laravel et renvoie un
// 503 propre (avec `Retry-After`) sur toutes les routes `api/*`. On intercepte
// globalement ce cas — ainsi qu'une coupure réseau / 502 sur une requête
// idempotente — pour afficher un overlay « mise à jour en cours » et réessayer
// automatiquement, sans déconnecter l'utilisateur (le token reste valide).
const MAINTENANCE_MAX_RETRIES = 5;
/** Plafond du backoff exponentiel, quand le serveur ne dit rien sur son délai de rétablissement. */
const RETRY_CAP_SECONDS = 15;
/**
 * Plafond de l'attente demandée par le serveur via `Retry-After`, plus élevé que celui du backoff.
 *
 * Les déploiements backend annoncent 60 s depuis le 23/08. Aligner cette valeur sur les 15 s du
 * backoff aveugle reviendrait à frapper cinq fois pendant leur fenêtre au lieu d'attendre une fois
 * qu'elle se referme — précisément l'inverse de ce que l'en-tête demande. Un serveur qui sait
 * combien de temps il sera absent en sait plus que notre heuristique.
 */
const RETRY_AFTER_CAP_SECONDS = 60;
/**
 * Budget d'attente cumulée, toutes reprises confondues.
 *
 * Le compteur de tentatives ne suffit plus depuis qu'on honore des `Retry-After` de 60 s : cinq
 * reprises représenteraient cinq minutes de bandeau, pour une fenêtre de déploiement annoncée à une
 * minute. Deux attentes pleines couvrent leur fenêtre deux fois — au-delà, ce n'est plus un
 * déploiement et mieux vaut rendre la main.
 *
 * Sans effet sur le backoff aveugle (2+4+8+15+15 = 44 s), qui reste sous ce plafond.
 */
const MAX_TOTAL_WAIT_SECONDS = 120;
/**
 * Plafond distinct pour le 502, volontairement bas.
 *
 * Une passerelle sans backend se résorbe en quelques secondes — le temps que PHP redémarre. Au-delà
 * c'est un vrai incident serveur, et poursuivre jusqu'aux ~44 s du plafond de maintenance ferait
 * patienter l'utilisateur pour rien avant de lui montrer l'erreur.
 */
const GATEWAY_MAX_RETRIES = 2;

// GET/HEAD/PUT/DELETE sont idempotents : on peut retenter une coupure réseau
// sans risque de double effet. POST/PATCH ne le sont pas : sur coupure réseau
// (la requête a pu être traitée avant la coupure) on laisse remonter l'erreur.
// Un 503, lui, est renvoyé avant tout traitement applicatif → toujours sûr à retenter.
function isIdempotent(method: string): boolean {
  const m = method.toUpperCase();
  return m === "GET" || m === "HEAD" || m === "PUT" || m === "DELETE";
}

function setMaintenance(active: boolean, cause?: ApiOutageCause): void {
  useMaintenanceStore.getState().setMaintenance(active, cause);
}

/**
 * Sommeil interruptible : une annulation ne doit pas attendre la fin du backoff, qui monte à 15 s.
 * Résout au lieu de rejeter — l'appelant relit `signal.aborted` juste après et décide lui-même.
 */
function sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const done = () => {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", done);
      resolve();
    };
    const timeout = setTimeout(done, ms);
    signal?.addEventListener("abort", done, { once: true });
  });
}

/**
 * Annulation demandée — démontage d'un composant, `AbortController` de l'appelant, requête coupée
 * par une navigation. Ce n'est pas un échec réseau : ni à retenter, ni à signaler comme incident.
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof Error && (error.name === "AbortError" || error.name === "TimeoutError");
}

/**
 * La page est en train de partir : les requêtes encore en vol vont être annulées par le navigateur
 * et rejeter exactement comme une coupure réseau.
 *
 * Ce n'est pas un incident — c'est nous qui provoquons le départ (déconnexion, 401, fin
 * d'impersonation, fermeture d'onglet). Sans ce drapeau, chaque requête en cours au moment de la
 * sortie produit sa propre remontée : trois d'un coup dans SUCCESCHOOL-FRONTEND-V2-E, pour un
 * simple clic sur « Retourner à mon compte ».
 *
 * Déclaré avant `fetchWithRetry`, qui le lit : en `let`, une lecture avant initialisation lèverait.
 * C'est aujourd'hui hors d'atteinte — la boucle n'est appelée qu'après l'évaluation du module — mais
 * l'ordre est ici une garantie plutôt qu'une coïncidence.
 */
let pageIsUnloading = false;

/**
 * À appeler juste avant une navigation plein écran déclenchée par l'application.
 *
 * `pagehide` couvre déjà la fermeture d'onglet et devrait précéder l'annulation des requêtes, mais
 * l'ordre exact n'est pas garanti selon les moteurs : marquer explicitement les sorties qu'on
 * déclenche soi-même rend le silence déterministe là où on sait qu'il est justifié.
 */
export function markPageUnloading(): void {
  pageIsUnloading = true;
}

/**
 * La page est-elle en train de partir ?
 *
 * Exposé pour les sondes qui vivent en dehors de la couche API et n'ont aucun autre moyen de savoir
 * qu'un échec est provoqué par une sortie plutôt que subi — typiquement l'abonnement aux canaux
 * temps réel, dont le refus n'a pas le même sens selon que la session expire ou non.
 */
export function isPageUnloading(): boolean {
  return pageIsUnloading;
}

/**
 * Horodatage du dernier réveil de la page (retour bfcache / avant-plan).
 *
 * Les requêtes suspendues pendant le gel ne rejettent pas à l'instant du gel mais à celui du
 * réveil, quand `pageIsUnloading` est déjà repassé à `false`. Une courte fenêtre de grâce après le
 * réveil rattrape cette rafale : ce sont des coupures provoquées par la mise en veille, pas des
 * incidents réseau (SUCCESCHOOL-FRONTEND-V2-…). Déclaré avant les écouteurs qui l'affectent, comme
 * `pageIsUnloading`.
 */
let pageResumedAt = 0;
const PAGE_RESUME_GRACE_MS = 3_000;

if (typeof window !== "undefined") {
  // Toute sortie de page — vraie navigation *ou* mise en cache bfcache (application quittée, écran
  // verrouillé, aller-retour vers Stripe sur iOS Safari) — annule les requêtes en vol. Les échecs
  // qui en résultent sont provoqués par la sortie, pas subis, et ne doivent pas remonter.
  //
  // On marquait autrefois `persisted: false` uniquement, de peur de verrouiller le drapeau à `true`
  // pour le reste de la session sur un gel bfcache. Ce risque n'existe plus : `pageshow` ci-dessous
  // remet systématiquement le drapeau à `false` au retour, bfcache compris. Sans le marquage sur
  // `persisted: true`, un retour de Stripe faisait remonter une rafale de `NetworkError` pour les
  // requêtes du dashboard tuées pendant l'aller (SUCCESCHOOL-FRONTEND-V2-…).
  window.addEventListener("pagehide", () => markPageUnloading());
  // Une page qu'on affiche est vivante, qu'il s'agisse d'un premier chargement ou d'une
  // restauration bfcache.
  window.addEventListener("pageshow", () => {
    pageIsUnloading = false;
    pageResumedAt = Date.now();
  });
  // Retour d'avant-plan sans rechargement (changement d'onglet, retour d'app) : mêmes requêtes
  // suspendues, même salve de rejets au réveil.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") pageResumedAt = Date.now();
  });
}

/**
 * `fetch` enveloppé d'une logique de retry pour le mode maintenance.
 * - 503 → retenté quelle que soit la méthode (aucun effet applicatif côté serveur).
 * - 502 → retenté seulement sur méthode idempotente, et deux fois au plus : la passerelle a pu
 *   tomber après avoir transmis la requête.
 * - Coupure réseau (fetch rejette) → retenté uniquement pour les méthodes idempotentes,
 *   sinon l'erreur est relancée pour que l'appelant en informe l'utilisateur.
 * Dès qu'une réponse n'appelle plus de reprise, l'état maintenance est levé.
 *
 * La boucle s'interrompt sur `init.signal` — y compris pendant le backoff, qui monte à 15 s — et
 * sur le départ de la page. Sans ça, une requête abandonnée continuait de retenter jusqu'à ~44 s
 * après que son écran a été quitté. Une annulation traverse telle quelle, jamais déguisée en
 * `NetworkError` : c'est ce qui permet à TanStack Query d'y voir une annulation et non un échec.
 */
export async function fetchWithRetry(url: string, init?: RequestInit): Promise<Response> {
  const idempotent = isIdempotent(init?.method ?? "GET");
  const signal = init?.signal;
  let attempt = 0;
  let waitedSeconds = 0;

  for (;;) {
    let res: Response | null = null;
    let networkError: unknown = null;
    try {
      res = await fetch(url, init);
    } catch (err) {
      networkError = err;
    }

    /**
     * Codes de passerelle transitoires, repris sur méthodes idempotentes uniquement.
     *
     * Le 502 vient de la passerelle et non de l'application : rien ne garantit que la requête n'a
     * pas atteint le backend avant sa chute, d'où la restriction aux méthodes sans effet de bord.
     *
     * Le 504 a rejoint la liste le 23/08, quand l'équipe backend a supprimé les 502 de sa procédure
     * de déploiement : celle-ci rend désormais 503 ou 504 selon l'instant, avec `Retry-After: 60`.
     * Sans cette ligne, leur correctif aurait silencieusement désactivé notre reprise — on
     * n'aurait plus vu passer le seul code qu'on surveillait. Ils garantissent que ces deux codes
     * signifient « requête non traitée », et jugent notre prudence sur les POST toujours justifiée :
     * on s'en tient donc à l'idempotence plutôt que d'élargir sur la foi de cette garantie.
     */
    const gatewayRetryable =
      (res?.status === 502 || res?.status === 504) && idempotent && attempt < GATEWAY_MAX_RETRIES;

    // Toute réponse HTTP qui n'appelle pas de reprise = l'API répond → on sort du mode maintenance.
    if (res && res.status !== 503 && !gatewayRetryable) {
      setMaintenance(false);
      return res;
    }

    const isNetwork = networkError !== null;

    // Annulation, départ de la page ou page en veille : plus personne n'attend cette réponse.
    // Retenter irait contre la demande, et le backoff ferait vivre la boucle jusqu'à ~44 s après
    // coup — c'est ce qui faisait remonter des erreurs d'écrans déjà quittés, avec la transaction de
    // la page d'arrivée.
    const pageAsleep =
      pageIsUnloading || (typeof document !== "undefined" && document.visibilityState === "hidden");
    if (isNetwork && (signal?.aborted || isAbortError(networkError) || pageAsleep)) {
      // Ne pas laisser le bandeau allumé sur une boucle qu'on vient d'interrompre.
      setMaintenance(false);
      throw networkError;
    }

    const withinBudget = waitedSeconds < MAX_TOTAL_WAIT_SECONDS;
    const retryable =
      withinBudget &&
      (gatewayRetryable ||
        ((res?.status === 503 || (isNetwork && idempotent)) && attempt < MAINTENANCE_MAX_RETRIES));

    if (!retryable) {
      // Retries épuisés ou coupure réseau non retentable.
      if (isNetwork) throw networkError; // laisse l'appelant gérer (ex. fetchMultipart)
      setMaintenance(false);
      return res!; // 502/503/504 persistant → renvoyé pour que handleResponse lève une ApiError
    }

    attempt += 1;
    // Une réponse HTTP, quel que soit son code, vient du serveur : déploiement ou incident côté API.
    //
    // Un échec de transport, lui, ne désigne personne : la requête n'a atteint personne, mais on ne
    // sait pas si c'est le réseau de l'utilisateur ou le serveur qui manque. Pendant le remplacement
    // des conteneurs, l'API est injoignable et ne peut même pas servir sa page de maintenance —
    // constaté en production sur un déploiement backend, où le bandeau envoyait vérifier une
    // connexion parfaitement fonctionnelle. Seul `navigator.onLine` à faux tranche sans ambiguïté :
    // l'appareil lui-même déclare ne plus avoir de réseau.
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    setMaintenance(true, !isNetwork ? "maintenance" : offline ? "offline" : "unreachable");

    // Respecte `Retry-After` quand le serveur en donne un — il sait, lui, combien de temps il sera
    // absent — sinon backoff exponentiel. Les deux plafonds sont distincts à dessein : le serveur a
    // le droit de demander une attente plus longue que celle qu'on s'impose à l'aveugle.
    const retryAfter = res ? Number(res.headers.get("Retry-After")) : NaN;
    const delaySeconds =
      Number.isFinite(retryAfter) && retryAfter > 0
        ? Math.min(retryAfter, RETRY_AFTER_CAP_SECONDS)
        : Math.min(2 ** attempt, RETRY_CAP_SECONDS);
    waitedSeconds += delaySeconds;
    await sleep(delaySeconds * 1000, signal);

    // Le sommeil a pu être écourté par une annulation survenue pendant l'attente : ne pas relancer
    // une tentative dont le résultat n'intéresse plus personne.
    if (signal?.aborted) {
      setMaintenance(false);
      throw signal.reason instanceof Error
        ? signal.reason
        : new DOMException("Requête annulée", "AbortError");
    }
  }
}

export class ApiError extends Error {
  status: number;
  fieldErrors: Record<string, string[]>;
  reasonCode?: string;
  /**
   * Id de l'événement Sentry correspondant, renseigné au moment de la capture.
   * Sert à afficher une référence courte à l'utilisateur (cf. `lib/error-kind.ts`),
   * pour que le support retrouve l'erreur exacte à partir d'un simple signalement.
   */
  eventId?: string;
  /**
   * `true` quand le corps de la réponse portait un `message` : le serveur a expliqué lui-même son
   * refus. Sinon le texte vient de `defaultMessageForStatus`, déduit du seul code de statut.
   *
   * La distinction est invisible depuis l'extérieur, et pourtant décisive : elle dit si l'on a le
   * droit de reformuler le message. Depuis que les déploiements backend rendent un 504 accompagné
   * de « Service temporairement indisponible, déploiement en cours », le requalifier en coupure
   * d'envoi mentirait à l'utilisateur.
   */
  messageFromServer?: boolean;
  /**
   * Charge utile `data` de la réponse d'erreur, quand le backend en fournit une.
   *
   * Un refus n'est pas toujours une impasse : certains endpoints renvoient, avec l'erreur, de quoi
   * poursuivre autrement. Cas d'usage actuel — `POST .../live-sessions/{id}/start` refuse un second
   * démarrage par un 422 qui porte la session déjà en cours, permettant de basculer sur « rejoindre »
   * sans requête supplémentaire (cf. `startOrResumeLiveSession`).
   *
   * Volontairement `unknown` : chaque appelant sait seul ce que son endpoint promet et doit valider
   * la forme avant de s'en servir.
   */
  data?: unknown;
  /**
   * Échec **délibérément** non remonté à Sentry (page qui s'en va, appareil hors ligne).
   *
   * Sans ce drapeau, se taire ici ne fait que déplacer le bruit : l'absence d'`eventId` est lue par
   * `logger.failure` comme « personne ne l'a encore signalé », et l'appelant produit alors
   * l'événement que la couche API venait justement d'éviter.
   */
  silenced?: boolean;
  /**
   * Valeur du header `Retry-After` (en secondes), quand le serveur en fournit un — typiquement sur
   * un 429. Permet à un appelant qui choisit de retenter de respecter le délai imposé par le
   * serveur plutôt que de retenter à l'aveugle sur un quota qui ne s'est pas encore régénéré.
   */
  retryAfterSeconds?: number;

  constructor(
    status: number,
    message: string,
    fieldErrors: Record<string, string[]> = {},
    reasonCode?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.reasonCode = reasonCode;
  }
}

/**
 * La requête n'a jamais atteint le serveur (wifi coupé, DNS, CORS, serveur injoignable).
 * `status = 0` la distingue d'une vraie réponse HTTP : sans ça un `TypeError: Failed to fetch`
 * brut remontait jusque dans l'UI et s'affichait comme une « erreur serveur ».
 */
export class NetworkError extends ApiError {
  /**
   * La réponse était arrivée, c'est la **lecture de son corps** qui a été coupée.
   *
   * Distinction utile au seul appelant capable d'y répondre : rejouer une requête idempotente coûte
   * autant que la première fois, alors qu'un échec d'envoi et un flux tronqué sont indiscernables
   * une fois enveloppés. Posé uniquement quand l'appelant a annoncé qu'il retenterait, donc jamais
   * visible au-delà de `fetchJson`.
   */
  bodyInterrupted?: boolean;

  constructor(message = "Connexion au serveur impossible.") {
    super(0, message);
    this.name = "NetworkError";
  }
}

/**
 * Remplace les identifiants variables par `:id` pour que le fingerprint Sentry regroupe
 * `/classrooms/12/sessions` et `/classrooms/47/sessions` dans la même issue.
 */
export function normalizeApiPath(path: string): string {
  return path
    .split("?")[0]
    .replace(/\/[0-9]+(?=\/|$)/g, "/:id")
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, "/:uuid");
}

interface RequestContext {
  path: string;
  method: string;
}

/**
 * L'appareil n'a plus d'interface réseau du tout.
 *
 * `navigator.onLine` a mauvaise réputation, mais son défaut est unidirectionnel : il annonce `true`
 * à tort (portail captif, réseau sans route) et jamais l'inverse. Un `false` est donc une
 * information sûre — il n'y a rien à joindre, et l'échec qui suit n'apprend rien.
 */
function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Durée pendant laquelle une même route en coupure ne produit qu'une remontée (cf. `toNetworkError`).
 *
 * Trente secondes couvrent largement une salve de reprises — l'anti-tempête du rejoin s'étale sur
 * une soixantaine de secondes — sans jamais masquer deux pannes réellement distinctes, qu'une
 * séance normale ne rapproche pas à ce point.
 */
const NETWORK_REPORT_COOLDOWN_MS = 30_000;
/**
 * Dernière remontée par couple (méthode, route normalisée).
 *
 * Borné par le nombre d'endpoints de l'application, donc sans croissance à surveiller — et remis à
 * zéro par le moindre rechargement, ce qui suffit à cet usage.
 */
const networkReportedAt = new Map<string, number>();

/** Enveloppe une coupure réseau en `NetworkError` après épuisement des retries. */
function toNetworkError(error: unknown, ctx: RequestContext, message?: string): NetworkError {
  const networkError = new NetworkError(message);
  // L'erreur est bien renvoyée à l'appelant — seule la remontée Sentry est tue.
  //
  // `pageIsUnloading` : sortie en cours. Fenêtre de grâce après réveil : les requêtes gelées
  // pendant une mise en veille (bfcache, arrière-plan) rejettent au retour, drapeau déjà relâché.
  // `hidden` : la page n'est pas au premier plan, un échec là n'intéresse personne.
  const resumingFromFreeze = pageResumedAt > 0 && Date.now() - pageResumedAt < PAGE_RESUME_GRACE_MS;
  const notForeground = typeof document !== "undefined" && document.visibilityState === "hidden";
  if (pageIsUnloading || resumingFromFreeze || notForeground) {
    networkError.silenced = true;
    return networkError;
  }
  /**
   * Hors ligne : un échec attendu, pas un incident.
   *
   * Une coupure mobile ne produit pas un échec mais une rafale — pendant la panne, tout ce qui
   * réessaie réessaie en vain. Le rejoin d'une séance live en est l'exemple extrême : jusqu'à six
   * tentatives, chacune enchaînant `/join` puis `/token`, soit une dizaine d'événements identiques
   * pour un seul élève et une seule coupure (SUCCESCHOOL-FRONTEND-V2-2V, où le SDK LiveKit avait
   * déjà renoncé après 68 s et dix essais).
   *
   * Le signal utile — « la connexion de cet élève a lâché » — est porté par la déconnexion live
   * elle-même, qui reste remontée. Ce qu'on tait ici, ce sont ses répliques.
   *
   * Journalisé en console malgré tout : le fil d'Ariane du replay doit garder trace des requêtes
   * tombées, sans quoi la panne devient un trou inexpliqué au milieu de la session.
   */
  const normalizedPath = normalizeApiPath(ctx.path);
  if (isOffline()) {
    logger.warn(`hors ligne — ${ctx.method} ${normalizedPath} non remonté`);
    networkError.silenced = true;
    return networkError;
  }

  /**
   * Signal de sortie de séance : un échec réseau ici est la conséquence d'une coupure, pas un incident.
   *
   * `POST /leave` est un signal de confort. Le backend clôture la séance sur de meilleurs
   * déclencheurs (coupure WebSocket, webhook, cron avec délai de grâce), et l'appel échoue
   * précisément quand le réseau de l'élève tombe — typiquement au moment où il quitte, l'appareil
   * se déclarant encore en ligne. La coupure elle-même est portée par la déconnexion live, qui reste
   * remontée ; seule sa réplique ne l'est plus ici (même appareil, même minute : `/settings/regions` de
   * LiveKit tombait en même temps). Les réponses HTTP en erreur de cette route, elles, restent
   * signalées — ne sont tues que les requêtes qui n'ont pas abouti.
   */
  if (ctx.method === "POST" && normalizedPath === "/v1/live-sessions/:uuid/leave") {
    logger.warn(
      `${ctx.method} ${normalizedPath} — coupure réseau non remontée (signal de confort)`,
    );
    networkError.silenced = true;
    return networkError;
  }

  /**
   * Répliques d'une coupure déjà signalée.
   *
   * `navigator.onLine` ne couvre que la panne franche. Le cas fréquent est ailleurs : une liaison
   * dégradée où certaines requêtes passent et d'autres non — SUCCESCHOOL-FRONTEND-V2-P, où LiveKit
   * et `/v1/conversations` répondaient `200` pendant que `/join` échouait, l'appareil se déclarant
   * en ligne à juste titre.
   *
   * Or ce qui réessaie réessaie en rafale : `attemptSilentRejoin` va jusqu'à six tentatives,
   * chacune enchaînant `/join` puis `/token`. Une douzaine d'événements pour un seul épisode, tous
   * déjà regroupés par l'empreinte ci-dessous — on ne perdait donc rien d'unique à les répéter,
   * sinon la lisibilité.
   *
   * Le premier échec sur une route est signalé, les suivants dans la foulée se taisent. Ce que
   * mesure l'issue cesse d'être « requêtes tombées » pour devenir « épisodes de coupure », ce qui
   * est l'unité utile — une panne de trente secondes ne doit pas peser douze fois plus lourd qu'une
   * panne d'une seconde. La console, elle, garde tout pour le replay.
   */
  const key = `${ctx.method} ${normalizedPath}`;
  const now = Date.now();
  const lastReportedAt = networkReportedAt.get(key);
  if (lastReportedAt !== undefined && now - lastReportedAt < NETWORK_REPORT_COOLDOWN_MS) {
    logger.warn(`coupure déjà signalée — ${key} non remonté (réplique)`);
    networkError.silenced = true;
    return networkError;
  }
  networkReportedAt.set(key, now);

  networkError.eventId = logger.error("Network error", networkError, {
    tags: { api_method: ctx.method, api_path: normalizedPath, api_status: 0 },
    extra: { cause: error instanceof Error ? error.message : String(error) },
    fingerprint: ["api", "network", ctx.method, normalizedPath],
  });
  return networkError;
}

// Note: useAuthStore is imported inline (not at module level) to prevent circular
// dependencies since auth.service.ts imports API_BASE_URL from this file.
export async function fetchJson<T>(path: string, options?: RequestInit): Promise<T> {
  const { useAuthStore } = await import("@/store/auth.store");
  const token = useAuthStore.getState().token;
  const ctx: RequestContext = { path, method: options?.method ?? "GET" };

  /**
   * Une seconde tentative quand le flux de la réponse est coupé en cours de lecture.
   *
   * `fetchWithRetry` ne couvre que la phase requête → réponse ; la lecture du corps se fait après
   * elle, dans `handleResponse`. Une liaison qui lâche juste avant la réponse était donc rejouée,
   * la même liaison lâchant juste après ne l'était pas — même cause, même méthode idempotente,
   * traitement opposé. Sans conséquence pour une lecture d'écran, que TanStack Query rejoue de son
   * côté ; coûteux pour les appels impératifs, dont `getLiveSessionToken` pendant un rejoin, où un
   * flux tronqué consommait une tentative de l'anti-tempête sur exactement la population visée —
   * mobile, réseau instable (SUCCESCHOOL-FRONTEND-V2-28 : Safari iOS, sept reprises LiveKit
   * d'affilée, puis un corps de réponse coupé net).
   *
   * Même règle que la boucle d'en dessous : méthodes idempotentes uniquement. Un `POST` coupé après
   * réponse a bel et bien été traité par le serveur.
   */
  const retryBodyRead = isIdempotent(ctx.method);

  for (let attempt = 0; ; attempt++) {
    let res: Response;
    try {
      res = await fetchWithRetry(`${API_BASE_URL}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options?.headers,
        },
      });
    } catch (fetchError) {
      // Une annulation traverse telle quelle : la maquiller en `NetworkError` ferait afficher « hors
      // ligne » pour un simple changement d'écran, et empêcherait TanStack Query de la reconnaître
      // comme une annulation plutôt que comme un échec.
      if (isAbortError(fetchError)) throw fetchError;
      // `fetchWithRetry` a déjà retenté (méthodes idempotentes) et relâché l'overlay
      // de maintenance : arrivé ici, la connexion est bel et bien coupée.
      throw toNetworkError(fetchError, ctx);
    }

    try {
      // Le marquage n'est demandé qu'au premier tour : la boucle ne peut donc pas dépasser deux
      // passages, et un second flux coupé repart en `NetworkError` signalée comme avant.
      return await handleResponse<T>(res, ctx, {
        deferBodyFailure: retryBodyRead && attempt === 0,
      });
    } catch (err) {
      if (err instanceof NetworkError && err.bodyInterrupted) continue;
      throw err;
    }
  }
}

export async function fetchMultipart<T>(
  path: string,
  formData: FormData,
  options?: RequestInit,
): Promise<T> {
  const { useAuthStore } = await import("@/store/auth.store");
  const token = useAuthStore.getState().token;

  const ctx: RequestContext = { path, method: options?.method ?? "POST" };

  let res: Response;
  try {
    res = await fetchWithRetry(`${API_BASE_URL}${path}`, {
      method: "POST",
      ...options,
      body: formData,
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
    });
  } catch (fetchError) {
    if (isAbortError(fetchError)) throw fetchError;
    // Le serveur peut couper la connexion sur un 413 avant d'envoyer les headers CORS,
    // ce qui provoque un TypeError plutôt qu'une réponse HTTP : on mentionne donc la
    // taille du fichier en plus de la connexion.
    throw toNetworkError(
      fetchError,
      ctx,
      "Envoi échoué — vérifiez votre connexion ou réduisez la taille du fichier.",
    );
  }

  try {
    return await handleResponse<T>(res, ctx);
  } catch (err) {
    /**
     * Un 502/504 sur un envoi de fichier ne signale presque jamais une panne applicative : c'est le
     * transfert qui n'est pas allé au bout. Nginx cesse d'attendre la suite du corps au bout de son
     * `client_body_timeout` et traduit la rupture en « Bad Gateway » — trompeur, puisque le serveur
     * applicatif était disponible et n'a même jamais vu la requête.
     *
     * Confirmé par l'équipe backend le 23/08 sur SUCCESCHOOL-FRONTEND-V2-4D : deux tentatives d'une
     * durée de 60,000 s exactement — la valeur du délai, au centième près — aucune n'ayant atteint
     * PHP, depuis un iPhone. Le message générique « Le serveur a rencontré un problème » envoyait
     * l'élève attendre un rétablissement qui ne viendrait jamais, au lieu de chercher un meilleur
     * réseau.
     *
     * Le message est réécrit sur place plutôt que reconstruit : `eventId`, `fieldErrors` et `data`
     * sont déjà renseignés et servent en aval (référence support, erreurs de formulaire).
     *
     * Toujours réécrit, même par-dessus une explication du serveur. On a cru un temps qu'un message
     * serveur méritait d'être préservé — un vrai déploiement backend renvoie un 503/504 portant
     * « Service temporairement indisponible, déploiement en cours ». Sentry 147969051 (23/09) a
     * montré que cette confiance était mal placée : ce texte vient de Traefik
     * (`docker/nginx/fallback.conf`), codé en dur pour *tout* 502/504 sur la passerelle, qu'il y ait
     * déploiement ou non — confirmé par l'équipe backend, aucun déploiement n'était en cours à
     * l'heure de cet incident. Il ne distingue donc jamais un vrai déploiement d'un upload coupé, et
     * pousser l'utilisateur à « attendre que ça passe » est aussi faux dans un cas que dans l'autre.
     */
    if (err instanceof ApiError && (err.status === 502 || err.status === 504)) {
      err.message =
        "Ta connexion s'est coupée pendant l'envoi du fichier. Réessaie, de préférence en Wi-Fi.";
    }
    throw err;
  }
}

/**
 * Récupère un fichier derrière authentification (le token passe en en-tête, un simple `<a href>`
 * ne peut donc pas le porter).
 *
 * Les erreurs passent par `handleResponse` : 401 → déconnexion, 422 → `fieldErrors`, etc., comme
 * pour `fetchJson`. Avec `fetch` (contrairement à axios en `responseType: "blob"`), le corps d'une
 * réponse en erreur reste lisible en JSON — aucun détour par `blob.text()` n'est nécessaire.
 *
 * `Accept` place `application/json` en premier : Laravel ne renvoie ses erreurs de validation en
 * JSON (au lieu d'une redirection) que si le premier type accepté est du JSON.
 *
 * `filename` vient de `Content-Disposition`, `null` si l'en-tête est absent ou illisible (en-tête
 * non exposé en CORS) : à l'appelant de fournir un nom de repli.
 */
export async function fetchBlob(
  path: string,
  options?: RequestInit,
): Promise<{ blob: Blob; filename: string | null }> {
  const { useAuthStore } = await import("@/store/auth.store");
  const token = useAuthStore.getState().token;
  const ctx: RequestContext = { path, method: options?.method ?? "GET" };

  let res: Response;
  try {
    res = await fetchWithRetry(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        Accept: "application/json, text/csv;q=0.9, */*;q=0.8",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
    });
  } catch (fetchError) {
    if (isAbortError(fetchError)) throw fetchError;
    throw toNetworkError(fetchError, ctx);
  }

  // Lève toujours sur `!res.ok` ; le `never` ci-dessous ne sert qu'à le dire au compilateur.
  if (!res.ok) return handleResponse<never>(res, ctx);

  let blob: Blob;
  try {
    blob = await res.blob();
  } catch (readError) {
    if (isAbortError(readError)) throw readError;
    throw toNetworkError(readError, ctx);
  }

  const filename =
    /filename="?([^";]+)"?/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? null;
  return { blob, filename };
}

export async function fetchDelete(path: string): Promise<void> {
  const { useAuthStore } = await import("@/store/auth.store");
  const token = useAuthStore.getState().token;
  const ctx: RequestContext = { path, method: "DELETE" };

  let res: Response;
  try {
    res = await fetchWithRetry(`${API_BASE_URL}${path}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch (fetchError) {
    if (isAbortError(fetchError)) throw fetchError;
    throw toNetworkError(fetchError, ctx);
  }

  // Passe par `handleResponse` comme les autres verbes : la branche impersonation du 401
  // et le parsing des erreurs de validation étaient auparavant dupliqués et divergents ici.
  await handleResponse<void>(res, ctx);
}

/** Lecture tolérante : le seul accès à `sessionStorage` lève si le stockage est bloqué (Safari). */
export function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Termine une session d'impersonation morte et redirige vers `/login`.
 *
 * Partagée entre le 401 REST (`handleResponse`, ci-dessous) et le refus d'abonnement Echo hors
 * sortie de page (`useLiveSessionChannel`) : les deux sont le même signal — un jeton
 * d'impersonation expiré, volontairement exclu du glissement sur activité côté backend, une
 * session d'observation ne devant jamais se prolonger indéfiniment même si l'onglet reste ouvert.
 *
 * Sans ce partage, seul le chemin REST savait réagir. Un onglet endormi pendant l'expiration ne
 * passe par aucun appel REST pour la découvrir, et se réveille en rejouant ses abonnements sur un
 * jeton mort — observé le 26/08 : quatre heures d'inactivité, puis quatre canaux resollicités d'un
 * coup à la reprise réseau (SUCCESCHOOL-FRONTEND-V2-3N). Terminer la session dès ce signal-là
 * évite exactement la boucle que le backend a relevée : sans lui, chaque reprise suivante aurait
 * rejoué le même abonnement voué à échouer.
 */
export async function endExpiredImpersonation(): Promise<void> {
  markPageUnloading();
  try {
    const { useImpersonationStore } = await import("@/store/impersonation.store");
    useImpersonationStore.getState().stop();
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_FLAG);
    // Sans effet dans la plupart des navigateurs pour un onglet non ouvert par script — c'est le
    // cas ici, l'impersonation navigue dans le même onglet. La redirection ci-dessous est le
    // véritable filet, comme dans le bouton « Retourner à mon compte ».
    window.close();
  } catch (err) {
    logger.warn("impersonation expirée — nettoyage impossible, purge directe du stockage", err);
    clearPersistedSession();
  }
  window.location.href = "/login";
}

/**
 * Repli quand le store d'auth n'a pas pu être chargé : vide directement l'état persisté, pour que
 * `/login` ne redémarre pas l'application sur une session morte (et ne reparte pas en boucle de 401).
 *
 * `AUTH_STORAGE` vit dans `localStorage`, ou dans `sessionStorage` en impersonation
 * (cf. `storage-keys.ts`) — on purge les deux sans chercher à savoir lequel est actif.
 */
function clearPersistedSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_STORAGE);
  } catch {
    // Stockage bloqué : la redirection reste le filet.
  }
  try {
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_STORAGE);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_FLAG);
  } catch {
    // idem
  }
}

async function handleResponse<T>(
  res: Response,
  ctx: RequestContext,
  /**
   * L'appelant s'engage à rejouer la requête si la lecture du corps est coupée : on lui remonte
   * alors l'échec marqué et **sans** remontée Sentry, plutôt que de signaler un incident dont la
   * tentative suivante va effacer la trace.
   */
  opts?: { deferBodyFailure?: boolean },
): Promise<T> {
  if (!res.ok) {
    if (res.status === 401) {
      // Expiration de session : c'est un événement normal, pas un incident — on laisse une
      // trace dans le fil d'Ariane Sentry (utile pour comprendre une erreur ultérieure)
      // sans créer d'issue.
      Sentry.addBreadcrumb({
        category: "auth",
        level: "info",
        message: "401 — session expirée",
        data: { path: normalizeApiPath(ctx.path), method: ctx.method },
      });

      // Marqué ici, avant tout `await` : cette branche se termine inconditionnellement par une
      // redirection, donc le départ est déjà décidé à cet instant.
      //
      // Le placer après le nettoyage laissait une fenêtre ouverte : l'import dynamique ci-dessous
      // rend la main à la boucle d'événements, et les sondes qui interrogent `isPageUnloading()`
      // depuis l'extérieur de cette couche y lisaient encore `false`. L'abonnement temps réel — dont
      // la requête d'autorisation est portée par Pusher, sans passer par ce gestionnaire — prenait
      // son propre 401 pile dans cet intervalle et le signalait comme une panne d'abonnement plutôt
      // que comme la simple expiration de session qu'il était (SUCCESCHOOL-FRONTEND-V2-3N).
      markPageUnloading();

      // Seule la redirection est non négociable : c'est elle qui sort l'utilisateur d'une session
      // morte, et son rechargement complet récupère au passage un `index.html` à jour.
      //
      // Le nettoyage des stores passe par des imports dynamiques (dépendance circulaire avec ce
      // fichier). Après un déploiement, ces chunks n'existent plus à leur ancienne URL : l'import
      // rejetait alors en emportant avec lui la redirection ET l'`ApiError`, laissant l'utilisateur
      // sur un écran dont chaque requête repartait en 401 sans jamais revenir au login
      // (SUCCESCHOOL-FRONTEND-V2-1D). Tout ce qui précède la redirection est donc best-effort.
      try {
        // Extrait dans `endExpiredImpersonation` : le refus d'abonnement Echo doit réagir de la
        // même façon à la même cause, cf. sa documentation.
        if (safeSessionGet(STORAGE_KEYS.IMPERSONATION_FLAG)) {
          await endExpiredImpersonation();
        } else {
          const { useAuthStore } = await import("@/store/auth.store");
          useAuthStore.getState().logout();
        }
      } catch (err) {
        // Sans purge de repli, `/login` rechargerait l'app sur un token périmé toujours persisté.
        logger.warn("401 — nettoyage de session impossible, purge directe du stockage", err);
        clearPersistedSession();
      }

      window.location.href = "/login";
      throw new ApiError(401, "Session expirée");
    }

    // Le battement « en train d'écrire » est un signal éphémère et sans état : une annonce perdue
    // n'a aucune conséquence, la suivante (2,5 s plus tard, cf. `useTypingIndicator`) rétablit
    // l'indicateur chez le destinataire. Son seau backend est partagé avec `mark-read` et s'épuise
    // en frappe rapide combinée à un changement de conversation ; le 429 qui en résulte est donc
    // attendu. On laisse un fil d'Ariane pour le contexte d'un incident ultérieur, sans créer
    // d'issue (SUCCESCHOOL-FRONTEND-V2-5P). Le `throw` est conservé : l'appelant `broadcastTyping`
    // n'attend pas la promesse, le rejet est ignoré silencieusement.
    if (
      res.status === 429 &&
      ctx.method === "POST" &&
      normalizeApiPath(ctx.path) === "/v1/conversations/:id/typing"
    ) {
      Sentry.addBreadcrumb({
        category: "api",
        level: "info",
        message: "429 sur /typing — annonce de saisie ignorée",
        data: { path: normalizeApiPath(ctx.path) },
      });
      throw new ApiError(res.status, defaultMessageForStatus(res.status));
    }

    // Message par défaut lisible : `${status} ${statusText}` faisait apparaître
    // « 500 Internal Server Error » tel quel dans les toasts. Le statut brut reste
    // disponible côté Sentry via les tags.
    let message = defaultMessageForStatus(res.status);
    let fromServer = false;
    let fieldErrors: Record<string, string[]> = {};
    let reasonCode: string | undefined;
    let data: unknown;
    try {
      const body = await res.json();
      if (body.message) {
        message = body.message;
        fromServer = true;
      }
      if (body.errors) fieldErrors = body.errors;
      reasonCode = body.reason_code as string | undefined;
      data = body.data;
    } catch {
      // pas de body JSON parsable, on garde le message par défaut
    }

    const apiError = new ApiError(res.status, message, fieldErrors, reasonCode);
    apiError.messageFromServer = fromServer;
    apiError.data = data;
    if (res.status === 429) {
      const retryAfter = Number(res.headers.get("Retry-After"));
      if (Number.isFinite(retryAfter) && retryAfter > 0) apiError.retryAfterSeconds = retryAfter;
    }
    const normalizedPath = normalizeApiPath(ctx.path);

    // Les endpoints `/v1/student/*` et `/v1/dashboard/student` sont réservés à un compte réellement
    // rattaché à un profil élève. Un compte dont le rôle persisté est `student` mais que le backend
    // ne reconnaît pas comme tel (désync de session, rôle obsolète) reçoit un 403
    // « Utilisateur n'est pas un étudiant. » sur *chaque* appel du tableau de bord élève, remonté en
    // masse dans Sentry (SUCCESCHOOL-FRONTEND-V2-8H). Rien à corriger côté client : on laisse un fil
    // d'Ariane pour le contexte d'un incident ultérieur, sans créer d'issue. L'UI affiche son état
    // d'erreur habituel via l'`ApiError` renvoyée.
    const isNonStudentAccess =
      res.status === 403 &&
      message === "Utilisateur n'est pas un etudiant." &&
      (normalizedPath.startsWith("/v1/student/") || normalizedPath === "/v1/dashboard/student");

    // 403 « e-mail non vérifié » : le backend refuse *tous* les endpoints protégés tant que
    // l'adresse n'est pas confirmée. Une arrivée sur une page qui déclenche plusieurs GET (factures,
    // échéanciers, paiements…) produisait donc une rafale d'issues Sentry pour un état connu
    // (SUCCESCHOOL-FRONTEND-V2-…). L'UI le gère déjà : l'événement `email-not-verified` émis plus bas
    // affiche la bannière de vérification. Fil d'Ariane, pas d'issue.
    const isEmailNotVerified =
      res.status === 403 && message === "Your email address is not verified.";

    // Un 422 dont le corps porte un `message` est une règle métier que le backend explique
    // lui-même à l'utilisateur : e-mail déjà sur la liste d'attente, place déjà réservée, créneau
    // pris… L'UI affiche ce texte (toast ou erreur de champ via `fieldErrors`) et il n'y a rien à
    // corriger côté client. Ces refus attendus saturaient Sentry (SUCCESCHOOL-FRONTEND-V2-…). On
    // laisse un fil d'Ariane pour le contexte d'un incident ultérieur, sans créer d'issue. Un 422
    // *sans* message serveur (données réellement malformées côté client) reste signalé.
    const isExpectedValidation = res.status === 422 && fromServer;

    // Révocation best-effort de l'ancien jeton push avant rotation (cf. `syncTokenWithBackend`
    // dans `usePushNotifications.ts`) : un second passage (StrictMode, montages concurrents) peut
    // retenter la suppression d'un jeton déjà supprimé par le premier. Le 404 qui en résulte est un
    // état attendu, sans conséquence — l'appelant l'avale déjà en best-effort — pas un incident à
    // signaler (SUCCESCHOOL-FRONTEND-V2-9V).
    const isStalePushTokenRevocation =
      res.status === 404 &&
      ctx.method === "DELETE" &&
      normalizedPath === "/v1/user/push-tokens/:id";

    if (
      isNonStudentAccess ||
      isEmailNotVerified ||
      isExpectedValidation ||
      isStalePushTokenRevocation
    ) {
      Sentry.addBreadcrumb({
        category: "api",
        level: "info",
        message: isExpectedValidation
          ? `422 règle métier — ${message}`
          : isEmailNotVerified
            ? "403 e-mail non vérifié — bannière de vérification"
            : isStalePushTokenRevocation
              ? "404 sur DELETE /push-tokens/:id — jeton déjà révoqué"
              : "403 compte non-élève — endpoint élève ignoré",
        data: { path: normalizedPath, method: ctx.method },
      });
      // `silenced` empêche `logger.failure` chez l'appelant de recréer l'issue qu'on vient d'éviter.
      if (isExpectedValidation || isEmailNotVerified || isStalePushTokenRevocation)
        apiError.silenced = true;
    } else {
      apiError.eventId = logger.error(`API error ${res.status}`, apiError, {
        tags: {
          api_status: res.status,
          api_method: ctx.method,
          api_path: normalizedPath,
          reason_code: reasonCode,
        },
        extra: { statusText: res.statusText, fieldErrors, url: res.url },
        // Sans fingerprint explicite, Sentry regroupe par message : tous les endpoints en
        // échec atterrissaient dans une seule issue. Une issue par (méthode, route, statut).
        fingerprint: ["api", ctx.method, normalizedPath, String(res.status)],
      });
    }

    if (message === "Your email address is not verified.") {
      const { useAuthStore: _authStore } = await import("@/store/auth.store");
      const { token, user } = _authStore.getState();
      if (token && user?.email_verified_at === null) {
        window.dispatchEvent(new CustomEvent("email-not-verified"));
      }
    }

    throw apiError;
  }

  // 204/205 et corps vide : `res.json()` lèverait un SyntaxError « Unexpected end of JSON
  // input » qui remontait jusqu'à l'utilisateur. Les DELETE sont concernés en premier lieu.
  if (res.status === 204 || res.status === 205 || res.headers.get("Content-Length") === "0") {
    return undefined as T;
  }

  // La réponse est arrivée (statut connu), mais la lecture de son flux peut encore échouer :
  // navigation en cours, onglet qui part, connexion coupée pendant le transfert. Safari lève alors
  // `TypeError: Load failed`. Cet appel vivait hors de tout `try` — et `handleResponse` est
  // lui-même appelé hors du `try` des `fetch*` — donc l'échec échappait à l'enveloppe
  // `NetworkError` dont c'est précisément le rôle : il remontait brut jusqu'à l'UI, sans statut
  // exploitable, et se regroupait dans Sentry en une issue unique pour tous les endpoints faute
  // de fingerprint (SUCCESCHOOL-FRONTEND-V2-K).
  try {
    return (await res.json()) as T;
  } catch (parseError) {
    // Lecture interrompue par une annulation : ni panne réseau, ni corps illisible.
    if (isAbortError(parseError)) throw parseError;
    // Flux interrompu : échec de transport, au même titre qu'un `fetch` qui rejette avant réponse.
    if (parseError instanceof TypeError) {
      // Rejouable : on rend la main sans rien signaler, l'appelant décide de la suite.
      if (opts?.deferBodyFailure) {
        const interrupted = new NetworkError();
        interrupted.bodyInterrupted = true;
        throw interrupted;
      }
      throw toNetworkError(parseError, ctx);
    }

    // Corps reçu mais illisible (JSON tronqué ou invalide) : le serveur a bien répondu, c'est un
    // incident côté API — ne pas le maquiller en coupure réseau, qui afficherait « hors ligne ».
    const normalizedPath = normalizeApiPath(ctx.path);
    const parseFailure = new ApiError(res.status, "Réponse illisible du serveur.");
    parseFailure.eventId = logger.error(`API response parse failed ${res.status}`, parseError, {
      tags: { api_status: res.status, api_method: ctx.method, api_path: normalizedPath },
      extra: { url: res.url, contentType: res.headers.get("Content-Type") },
      fingerprint: ["api", "parse", ctx.method, normalizedPath],
    });
    throw parseFailure;
  }
}

function defaultMessageForStatus(status: number): string {
  if (status >= 500) return "Le serveur a rencontré un problème.";
  if (status === 404) return "Ressource introuvable.";
  if (status === 403) return "Vous n'avez pas accès à cette ressource.";
  if (status === 419 || status === 409) return "La page n'est plus à jour, veuillez réessayer.";
  if (status === 413) return "Fichier trop volumineux pour le serveur.";
  if (status === 422) return "Certaines informations sont invalides.";
  if (status === 429) return "Trop de requêtes, patientez un instant.";
  return "La requête n'a pas abouti.";
}
